import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { ModuleRoute } from '@core/router/types';
import { Permissions } from '@core/auth/permissions';

// Lazy-loaded page components for code splitting
const UserListPage = lazy(() => import('./pages/UserListPage'));
const UserFormPage = lazy(() => import('./pages/UserFormPage'));
const EmployeeAccountsPage = lazy(() => import('./pages/EmployeeAccountsPage'));
const UserRolesPage = lazy(() => import('./pages/UserRolesPage'));
const UserPermissionsPage = lazy(() => import('./pages/UserPermissionsPage'));

/**
 * Users Module Routes
 *
 * All routes are relative to /users base path.
 * Routes with permission prop require that permission to access.
 * 
 * This module handles:
 * - User management (create, update, archive users)
 * - Role management (assign roles to users)
 * - Permission management (assign permissions to users/roles)
 */
export const usersRoutes: ModuleRoute[] = [
  // Default redirect to user list
  {
    index: true,
    element: <Navigate to="lista" replace />,
  },

  // User List (Korisnici)
  {
    path: 'lista',
    element: <UserListPage />,
    permission: Permissions.Users.Users.Read,
    meta: {
      title: 'Korisnici',
      icon: 'People',
      showInSidebar: true,
      order: 1,
    },
  },

  // Employee account status (Nalozi zaposlenih)
  {
    path: 'nalozi-zaposlenih',
    element: <EmployeeAccountsPage />,
    permission: Permissions.Users.Users.Read,
    meta: {
      title: 'Nalozi zaposlenih',
      icon: 'Badge',
      showInSidebar: true,
      order: 2,
    },
  },

  // Create User
  {
    path: 'novi',
    element: <UserFormPage />,
    permission: Permissions.Users.Users.Create,
    meta: {
      title: 'Novi korisnik',
      showInSidebar: false,
    },
  },

  // Edit User
  {
    path: ':id/izmeni',
    element: <UserFormPage />,
    permission: Permissions.Users.Users.Update,
    meta: {
      title: 'Izmeni korisnika',
      showInSidebar: false,
    },
  },

  // Roles (Uloge) - To be implemented later
  {
    path: 'uloge',
    element: <UserRolesPage />,
    permission: Permissions.Users.Roles.Read,
    meta: {
      title: 'Uloge',
      icon: 'Security',
      showInSidebar: true,
      order: 3,
    },
  },

  // Permissions (Permisije) - To be implemented later
  {
    path: 'permisije',
    element: <UserPermissionsPage />,
    permission: Permissions.Users.Permissions.Assign,
    meta: {
      title: 'Permisije',
      icon: 'VpnKey',
      showInSidebar: true,
      order: 4,
    },
  },
];

